import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus, Parameter} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {defaultTo, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useGetArticleQuery,
  useGetHandlingUnitsQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  handleApiError,
  NavigationItem,
  Section,
  useChecklist,
  usePermissions,
} from '@dms/shared';
import {getHandlingUnitLabel} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Availability} from './(sections)/Availability/Availability';
import {Documents} from './(sections)/Documents/Documents';
import {Movements} from './(sections)/Movements/Movements';
import {Overview} from './(sections)/Overview/Overview';
import {Receipts} from './(sections)/Receipts/Receipts';
import {Reservations} from './(sections)/Reservations/Reservations';
import {SupplierArticles} from './(sections)/SupplierArticles/SupplierArticles';
import {UpdateJobsLog} from './(sections)/UpdateJobsLog/UpdateJobsLog';

export function ArticleDetail() {
  const params = useWarehouseParams();
  const currencyFormatter = useFormatCurrency();
  const navigate = useNavigate();
  const dateFormatter = useDateTimeFormatter();

  const [articleEdit] = usePermissions({
    permissionKeys: ['articleEdit'],
  });

  const articleQuery = useGetArticleQuery({
    articleId: params.articleId,
    warehouseId: params.warehouseId,
  });

  const {
    data: article,
    isLoading: isArticleLoading,
    isFetching: isArticleFetching,
    isError: isArticleError,
  } = articleQuery;

  const headerActionsQuery = useGetWarehouseHeaderDynamicActionsQuery(
    {id: params.articleId, resource: 'article'},
    {skip: isNilOrEmpty(params.articleId)}
  );

  const [putHeaderAction, headerActionStatus] = usePutWarehouseHeaderDynamicActionsMutation();

  const {
    data: warehouse,
    isLoading: isWarehouseLoading,
    isError: isWarehouseError,
  } = useGetWarehouseQuery(
    {warehouseId: article?.warehouseId as string},
    {skip: isNilOrEmpty(article)}
  );

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const {data: tenant, isLoading: isTenantLoading, isError: isTenantError} = useGetTenantQuery();

  const {
    data: handlingUnits,
    isLoading: isHandlingUnitsLoading,
    isError: isHandlingUnitsError,
  } = useGetHandlingUnitsQuery();

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-article', contextId: params.articleId},
    {skip: !params.articleId}
  );

  const {
    data: createdByUser,
    isLoading: isCreatedByUserLoading,
    isError: isCreatedByUserError,
  } = useGetUserQuery({id: article?.createdBy as string}, {skip: isNilOrEmpty(article?.createdBy)});

  const {
    data: updatedByUser,
    isLoading: isUpdatedByUserLoading,
    isError: isUpdatedByUserError,
  } = useGetUserQuery({id: article?.updatedBy as string}, {skip: isNilOrEmpty(article?.updatedBy)});

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: params.articleId,
    resourceId: 'WAREHOUSE_ARTICLE',
  });

  if (
    !article ||
    !warehouses ||
    !tenant ||
    !warehouse ||
    !handlingUnits ||
    !createdByUser ||
    !updatedByUser
  ) {
    return (
      <DataStatus
        isLoading={
          isArticleLoading ||
          isWarehousesLoading ||
          isTenantLoading ||
          isWarehouseLoading ||
          isCreatedByUserLoading ||
          isUpdatedByUserLoading ||
          isHandlingUnitsLoading
        }
        isError={
          isArticleError ||
          isWarehousesError ||
          isTenantError ||
          isWarehouseError ||
          isCreatedByUserError ||
          isUpdatedByUserError ||
          isHandlingUnitsError
        }
        minHeight="100vh"
      />
    );
  }

  const priceWithoutVat = defaultTo(0, article?.dispensingPrices?.saleBasePriceWithoutVat);
  const priceWithVat = defaultTo(0, article?.dispensingPrices?.saleBasePriceWithVat);
  const currencyWithVat = currencyFormatter(priceWithVat, tenant.currency, 2);
  const currencyWithoutVat = currencyFormatter(priceWithoutVat, tenant.currency, 2);
  const createdDate = dateFormatter('dateTimeMedium', parseDate(article.created));
  const updatedDate = isNotNil(article.updated)
    ? dateFormatter('dateTimeMedium', parseDate(article.updated))
    : EMPTY_PLACEHOLDER;

  const handlingUnitLabel = getHandlingUnitLabel(article.handlingUnit, handlingUnits);

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'reserve') {
      handleReserve();
      return;
    }

    putHeaderAction({
      id: params.articleId,
      resource: 'article',
      body: {actionKey},
    })
      .unwrap()
      .then(() => {
        articleQuery.refetch();
      })
      .catch(handleApiError);
  };

  const handleReserve = () =>
    navigate(
      composePath(warehouseRoutes.articleDetailReservations, {
        params: {warehouseId: params.warehouseId, id: params.articleId},
      })
    );

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser) ?? EMPTY_PLACEHOLDER}`;
  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUser) ?? EMPTY_PLACEHOLDER}`;

  const dynamicActions = createHeaderActions({
    actions: headerActionsQuery.data?.actions,
    callback: handleActionClick,
    isLoading: headerActionStatus.isLoading,
  });

  const getHeader: DetailTemplateHeader = {
    title: article.name,
    isEntityLoading: isArticleFetching || headerActionsQuery.isFetching,
    icon: 'custom/warehouse',
    parameters: buildArray<Parameter>()
      .add(article.manufacturerNumber)
      .whenNotNil(article.manufacturerNumberNormalized)
      .add(`${i18n.t('general.labels.created')}: ${createdDate}`)
      .add(createdBy)
      .add(updatedBy)
      .when(article.updated, `${i18n.t('general.labels.lastUpdate')}: ${updatedDate}`)
      .add(warehouse?.name)
      .when(article.storageLocation, article.storageLocation),
    recordId: article.id,
    actions: dynamicActions,
    controls: ['ASSIGNEE'],
    primaryParameter: defaultTo(undefined, currencyWithVat),
    secondaryParameter: `${currencyWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
    alerts: !articleEdit
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingArticleEditPermission'),
          },
        ]
      : undefined,
  };

  const navigation = buildArray<NavigationItem>()
    .add({
      id: 'article',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.articleDetailOverview, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Overview
          article={article}
          warehouse={warehouse}
          warehouses={warehouses}
          tenant={tenant}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailOverview('sections.article')}
        />
      ),
      hasSeparator: true,
      'data-testid': testIds.warehouse.articleDetailOverview('article'),
    })
    .add({
      id: 'availability',
      label: i18n.t('entity.warehouse.labels.availability'),
      href: composePath(warehouseRoutes.articleDetailAvailability, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Availability
          article={article}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailAvailability('sections.availability')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailAvailability('availability'),
    })
    .add({
      id: 'receipts',
      label: i18n.t('entity.warehouse.labels.receipts'),
      href: composePath(warehouseRoutes.articleDetailReceipts, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Receipts
          article={article}
          tenant={tenant}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailReceipts('sections.receipts')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailReceipts('receipts'),
    })
    .add({
      id: 'movements',
      label: i18n.t('entity.warehouse.labels.movements'),
      href: composePath(warehouseRoutes.articleDetailMovements, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Movements data-testid={testIds.warehouse.articleDetailMovements('sections.movements')} />
      ),
      'data-testid': testIds.warehouse.articleDetailMovements('movements'),
    })
    .add({
      id: 'reservations',
      label: i18n.t('entity.warehouse.labels.reservations'),
      href: composePath(warehouseRoutes.articleDetailReservations, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Reservations
          article={article}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailReservations('sections.reservations')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailReservations('reservations'),
    })
    .add({
      id: 'supplierArticles',
      label: i18n.t('entity.warehouse.labels.supplierArticles'),
      href: composePath(warehouseRoutes.articleDetailSupplierArticles, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <SupplierArticles
          authorizationProfileId={warehouse?.authorizationProfileId}
          data-testid={testIds.warehouse.articleDetailSupplierArticles('sections.supplierArticles')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailSupplierArticles('supplierArticles'),
    })
    .add({
      id: 'updateJobsLog',
      label: i18n.t('entity.warehouse.labels.updateJobsLog'),
      href: composePath(warehouseRoutes.articleDetailUpdateJobsLog, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <UpdateJobsLog
          data-testid={testIds.warehouse.articleDetailUpdateJobsLog('sections.updateJobsLog')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailUpdateJobsLog('updateJobsLog'),
    })
    .add({
      id: 'documents',
      label: i18n.t('entity.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.articleDetailDocuments, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.articleDetailDocuments('sections.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.articleDetailDocuments('documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'checklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.articleDetailChecklists, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="warehouse-article"
            contextId={params.articleId}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.articleDetailChecklists('checklists'),
    });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.articleDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(article)}
        isLoading={isArticleLoading || headerActionsQuery.isLoading}
        isError={isArticleError || headerActionsQuery.isError}
        isLocked={headerActionStatus.isLoading}
        header={getHeader}
        navigation={navigation}
        data-testid={testIds.warehouse.articleDetailOverview('page')}
      />
    </>
  );
}
